import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { useTranslation } from 'react-i18next';
import { Title } from '../components/ui/Title';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { DateTimePicker } from '../components/DateTimePicker';
import { LocationAutocomplete } from '../components/LocationAutocomplete';
import { BandMultiSelect } from '../components/filters/BandMultiSelect';
import type { Event, Band } from '../types/database';

const editEventSchema = z.object({
  title: z.string().min(3).max(100),
  description: z.string().min(10).optional(),
  location: z.string().min(3),
  coordinates: z.object({
    latitude: z.number(),
    longitude: z.number(),
  }),
  event_date: z.date().min(new Date(), { message: "Event date must be in the future" }),
});

type EditEventFormData = z.infer<typeof editEventSchema>;

function EditEvent() {
  const { t } = useTranslation();
  const { eventSlug } = useParams<{ eventSlug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile } = useProfile();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bands, setBands] = useState<Band[]>([]);
  const [selectedBands, setSelectedBands] = useState<Band[]>([]);
  const [bandsError, setBandsError] = useState('');

  const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm<EditEventFormData>({
    resolver: zodResolver(editEventSchema),
  });

  useEffect(() => {
    fetchBands();
    loadEvent();
  }, [eventSlug]);

  const fetchBands = async () => {
    try {
      const { data, error } = await supabase
        .from('bands')
        .select('*')
        .eq('is_claimed', true)
        .order('name');

      if (error) throw error;
      setBands(data || []);
    } catch (err) {
      console.error('Error fetching bands:', err);
    }
  };

  const loadEvent = async () => {
    if (!eventSlug) return;

    try {
      const { data: eventData, error: eventError } = await supabase
        .from('events')
        .select('*')
        .eq('slug', eventSlug)
        .single();

      if (eventError) throw eventError;
      if (!eventData) throw new Error('Event not found');

      const { data: eventBands, error: eventBandsError } = await supabase
        .from('event_bands')
        .select('band_id, bands(*)')
        .eq('event_id', eventData.id);

      if (eventBandsError) throw eventBandsError;

      const bandsData = eventBands?.map(eb => eb.bands).filter(Boolean) || [];

      setEvent(eventData);
      setSelectedBands(bandsData as Band[]);
      reset({
        title: eventData.title,
        description: eventData.description || '',
        location: eventData.location,
        coordinates: eventData.coordinates,
        event_date: new Date(eventData.event_date),
      });
    } catch (err) {
      console.error('Error loading event:', err);
      setError(err instanceof Error ? err.message : 'Failed to load event');
    } finally {
      setLoading(false);
    }
  };

  const handleLocationChange = (place: { name: string; coordinates: { latitude: number; longitude: number } }) => {
    setValue('location', place.name);
    setValue('coordinates', place.coordinates);
  };

  const handleInviteBand = async (bandName: string, email: string) => {
    try {
      const slug = bandName.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();

      const { data: band, error: bandError } = await supabase
        .from('bands')
        .insert({
          name: bandName,
          slug: `${slug}-${Date.now()}`,
          is_claimed: false,
          status: 'Aktiv',
        })
        .select()
        .single();

      if (bandError) throw bandError;

      const inviteToken = crypto.randomUUID();
      const { error: inviteError } = await supabase
        .from('invitations')
        .insert({
          band_id: band.id,
          email: email,
          invitation_token: inviteToken,
          status: 'pending',
        });

      if (inviteError) throw inviteError;

      const { error: functionError } = await supabase.functions.invoke('send-band-claim-invitation', {
        body: {
          email,
          bandName,
          inviteToken,
        },
      });

      if (functionError) throw functionError;

      setSelectedBands([...selectedBands, band]);
      await fetchBands();
    } catch (err) {
      console.error('Error inviting band:', err);
      throw err;
    }
  };

  const onSubmit = async (data: EditEventFormData) => {
    if (isSubmitting) return;

    if (!event) return;

    if (selectedBands.length === 0) {
      setBandsError('Bitte wähle mindestens eine Band aus');
      return;
    }

    try {
      setIsSubmitting(true);
      setBandsError('');

      const { error: updateError } = await supabase
        .from('events')
        .update({
          title: data.title,
          description: data.description,
          location: data.location,
          coordinates: data.coordinates,
          event_date: data.event_date.toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', event.id);

      if (updateError) throw updateError;

      await supabase
        .from('event_bands')
        .delete()
        .eq('event_id', event.id);

      const eventBands = selectedBands.map(band => ({
        event_id: event.id,
        band_id: band.id,
      }));

      const { error: eventBandsError } = await supabase
        .from('event_bands')
        .insert(eventBands);

      if (eventBandsError) throw eventBandsError;

      navigate('/agenda');
    } catch (err) {
      console.error('Error updating event:', err);
      setError(err instanceof Error ? err.message : 'Failed to update event');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-6 pb-8 py-24">
        <div className="text-center text-light/60">{t('common.loading')}</div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="container mx-auto px-6 pb-8 md:py-24">
        <div className="text-center text-red-500">{error || 'Event not found'}</div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: t('nav.agenda'), href: '/agenda' },
    { label: t('events.editEvent.title') },
  ];

  return (
    <div className="container mx-auto px-6 pb-8 md:py-24">
      <div className="mb-6">
        <Breadcrumbs items={breadcrumbItems} />
      </div>

      <Title subtitle={t('events.editEvent.subtitle')}>
        {t('events.editEvent.title')}
      </Title>

      <div className="max-w-4xl mx-auto">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          <div className="panel">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-light/80 mb-2">
                  Bands
                </label>
                <BandMultiSelect
                  bands={bands}
                  selectedBands={selectedBands}
                  onSelectBands={setSelectedBands}
                  onInviteBand={handleInviteBand}
                  placeholder="Wähle Bands aus..."
                  error={bandsError}
                />
                <p className="mt-1 text-sm text-light/60">
                  Du kannst mehrere Bands auswählen oder eine noch nicht registrierte Band per E-Mail einladen
                </p>
              </div>

              <div>
                <label htmlFor="title" className="block text-sm font-medium text-light/80 mb-2">
                  {t('events.form.title')}
                </label>
                <input
                  type="text"
                  id="title"
                  {...register('title')}
                  className="mt-1 block w-full rounded-lg bg-dark border border-gray-custom/20 px-4 py-2 text-light focus:border-gray-custom/40 focus:outline-none"
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-500">{errors.title.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-light/80 mb-2">
                  {t('events.form.description')}
                </label>
                <textarea
                  id="description"
                  rows={4}
                  {...register('description')}
                  className="mt-1 block w-full rounded-lg bg-dark border border-gray-custom/20 px-4 py-2 text-light focus:border-gray-custom/40 focus:outline-none"
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-500">{errors.description.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-light/80 mb-2">
                  {t('events.form.location')}
                </label>
                <LocationAutocomplete
                  value={watch('location') || ''}
                  onChange={handleLocationChange}
                  error={errors.location?.message}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-light/80 mb-2">
                  {t('events.form.dateTime')}
                </label>
                <DateTimePicker
                  selected={watch('event_date')}
                  onChange={(date) => setValue('event_date', date)}
                  minDate={new Date()}
                  error={errors.event_date?.message}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-end md:space-x-4">
            <button
              type="button"
              onClick={() => navigate('/agenda')}
              className="btn-outline order-2 mt-6 md:mt-0"
              disabled={isSubmitting}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="btn-secondary md:order-2"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('common.saving') : t('forms.saveChanges')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditEvent;